// Desktop mega menu: click, keyboard, aria-expanded, outside click, Escape and theme editor support.
// Hover opening is CSS only (component-header-main.css); a panel shows while aria-expanded is true or on hover.

// Some browsers (e.g. Brave with fingerprinting protection, remote desktops) report no hover/pointer support
// to the media queries even with a real mouse, so hover is enabled once real mouse movement is detected.
// The first mouse-type pointer event (or a mousemove without Pointer Events) adds pv-has-mouse
// to <html>, which the CSS hover rules and canHover() also accept; a touch removes it again (hybrid devices).
// Touch-only devices never fire mouse-type pointer events, so they never get hover.
(() => {
  const root = document.documentElement;
  let lastTouch = 0;
  const onPointer = (event) => {
    if (event.pointerType === 'mouse') root.classList.add('pv-has-mouse');
  };
  document.addEventListener('pointermove', onPointer, { passive: true });
  document.addEventListener('pointerover', onPointer, { passive: true });
  document.addEventListener(
    'touchstart',
    () => {
      lastTouch = Date.now();
      root.classList.remove('pv-has-mouse');
    },
    { passive: true }
  );
  // No Pointer Events: a mousemove not following a touch (which fires compatibility mouse events) is a mouse.
  if (!window.PointerEvent) {
    document.addEventListener('mousemove', () => {
      if (Date.now() - lastTouch > 1000) root.classList.add('pv-has-mouse');
    });
  }
})();

class MegaNav extends HTMLElement {
  connectedCallback() {
    this.syncActiveItem();

    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    if (!this.triggers.length) return;

    this.panels = this.triggers.map((trigger) => this.panelFor(trigger)).filter(Boolean);
    this.scrim = this.querySelector('[data-mega-scrim]');
    this.activeTrigger = null;
    this.desktopQuery = window.matchMedia('(min-width: 769px)');
    // Same conditions as the CSS hover rules (see canHover()).
    this.hoverQuery = window.matchMedia('(any-hover: hover) and (any-pointer: fine) and (min-width: 990px)');
    this.wideQuery = window.matchMedia('(min-width: 990px)');

    this.triggers.forEach((trigger) => {
      const item = trigger.parentElement;
      // Pressing another item moves focus before its click switches panels; focusout leaves that to the click.
      trigger.addEventListener('pointerdown', () => (this.pressedTrigger = trigger));
      trigger.addEventListener('click', () => {
        this.pressedTrigger = null;
        // A panel hover opened isn't expanded yet, so this click keeps it open (and expands it).
        this.activeTrigger === trigger ? this.close() : this.open(trigger);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.open(trigger);
        this.panelFor(trigger)?.querySelector('a[href], button')?.focus();
      });
      // When the mouse leaves the item: a click-opened panel closes like a hovered one (CSS adds the 250ms
      // delay), and a panel closed by click or Escape can open on hover again. Touch is ignored: on a
      // touchscreen laptop its pointerleave fires right after each tap, which would undo the tap's toggle.
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        item.removeAttribute('data-mega-dismissed');
        // Only collapses aria-expanded, so the CSS close delay still applies.
        if (this.activeTrigger === trigger && this.canHover()) this.collapse();
      });
      // A panel closed by a tap stays closed under the browser's lingering touch hover; the mouse arriving
      // later should still open it on hover. Any item entered also ends the instant scrim hide of close().
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        item.removeAttribute('data-mega-dismissed');
        this.removeAttribute('data-mega-closed');
      });
    });

    // Focusing a top-level link without a panel closes whatever panel is open.
    this.querySelectorAll('.header__nav-item > a.header__menu-item').forEach((link) => {
      link.addEventListener('focus', () => {
        if (this.activeTrigger) this.close();
      });
    });

    // The × button closes a panel opened by hover too. After a keyboard press (detail 0) close() moves focus
    // back to the item; a mouse click drops focus instead, so nothing is focused and :focus-within can't hold it.
    this.querySelectorAll('[data-mega-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (event.detail !== 0) button.blur();
        this.close();
      });
    });

    this.onDocumentClick = (event) => {
      if (this.activeTrigger && !this.contains(event.target)) this.close();
    };
    this.onFocusOut = (event) => {
      // Safari doesn't focus links or buttons on mouse click, so focus falling back to the body
      // (no relatedTarget) is not the visitor leaving the menu. Mouse leave and outside click
      // handle that; this only closes when keyboard focus moves out of the open item and its panel.
      const next = event.relatedTarget;
      if (!next || !this.activeTrigger) return;
      if (next === this.activeTrigger || next === this.pressedTrigger) return;
      if (this.panelFor(this.activeTrigger)?.contains(next)) return;
      this.close();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (!this.activeTrigger && !this.showingItems().length) return;
      event.preventDefault();
      // Back to the item, unless focus is somewhere else on the page (e.g. a field).
      const focus = document.activeElement;
      this.close(!focus || focus === document.body || this.contains(focus));
    };
    this.onBreakpointChange = () => this.close();

    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('keydown', this.onKeydown);
    // Safari < 14 only has the older addListener API; throwing here would skip the setup below.
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    } else {
      this.desktopQuery.addListener(this.onBreakpointChange);
    }

    if (this.scrim) this.scrim.addEventListener('click', () => this.close());

    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const panel = event.target.closest?.('.mega-panel');
        if (!panel || !this.contains(panel)) return;
        const trigger = this.triggers.find((t) => t.getAttribute('aria-controls') === panel.id);
        if (trigger) this.open(trigger, true);
      };
      this.onBlockDeselect = (event) => {
        if (event.target.closest?.('.mega-panel')) this.close();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    if (!this.triggers?.length) return;
    document.removeEventListener('click', this.onDocumentClick);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    } else {
      this.desktopQuery.removeListener(this.onBreakpointChange);
    }
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  // Hover opens panels when the media query matches, or a mouse has been seen (pv-has-mouse) on a wide screen.
  canHover() {
    if (this.hoverQuery.matches) return true;
    return document.documentElement.classList.contains('pv-has-mouse') && this.wideQuery.matches;
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  // The same page can sit under several top-level items. Remember which one the visitor went
  // through; the inline script in header-dropdown-menu.liquid applies it before first paint.
  syncActiveItem() {
    const storageKey = 'pv-nav-active';
    const items = Array.from(this.querySelectorAll('.header__nav-item'));
    const topItemOf = (item) => item.querySelector(':scope > .header__menu-item');

    items.forEach((item) => {
      item.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        const top = topItemOf(item);
        if (!link || !top?.id) return;
        try {
          const url = new URL(link.href, window.location.href);
          sessionStorage.setItem(storageKey, JSON.stringify({ key: url.pathname + url.hash, id: top.id }));
        } catch (error) {}
      });
    });
  }

  // Open by click/keyboard or by hover (CSS). The sticky header (sections/header.liquid) stays put while true.
  isOpen() {
    return !!this.activeTrigger || this.panels.some((panel) => getComputedStyle(panel).visibility === 'visible');
  }

  // Only one overlay at a time: the search popup and the cart drawer / notification give way to the menu.
  closeOtherOverlays() {
    document.querySelector('search-dialog')?.close?.();
    if (document.getElementById('cart-notification')?.classList.contains('active')) {
      document.querySelector('cart-notification')?.close?.();
    }
    document.querySelector('cart-drawer.active')?.close?.();
  }

  // Items whose panel is showing (by click, hover, focus or the close delay) or about to open on hover.
  showingItems() {
    return this.triggers
      .filter((t) => t.parentElement.matches(':hover') || getComputedStyle(this.panelFor(t)).visibility === 'visible')
      .map((t) => t.parentElement);
  }

  open(trigger, force = false) {
    if (!force && !this.desktopQuery.matches) return;
    if (!this.activeTrigger) this.closeOtherOverlays();
    this.activeTrigger = trigger;
    trigger.parentElement.removeAttribute('data-mega-dismissed');
    this.removeAttribute('data-mega-closed');
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', String(t === trigger)));
  }

  // aria-expanded only: a panel still under the pointer stays (hover), and one that isn't closes after the CSS delay.
  collapse() {
    this.activeTrigger = null;
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  // Closes now, including a panel held open by hover, focus inside or the close delay: data-mega-dismissed
  // hides it until the mouse leaves its item (or it is opened again), and data-mega-closed hides the scrim.
  close(returnFocus = false) {
    const previous = this.activeTrigger;
    const items = this.showingItems();
    this.collapse();
    if (!previous && !items.length) return;

    items.forEach((item) => item.setAttribute('data-mega-dismissed', ''));
    this.setAttribute('data-mega-closed', '');
    // Focus left inside a closed panel goes back to its item.
    const focusTrigger = this.triggers.find((t) => this.panelFor(t)?.contains(document.activeElement));
    if (focusTrigger) focusTrigger.focus();
    else if (returnFocus && previous) previous.focus();
  }
}

if (!customElements.get('mega-nav')) customElements.define('mega-nav', MegaNav);
